/**
 * Builds the immutable snapshot the viewport search policies evaluate for one
 * settled map position.
 *
 * Pure — the core reads the camera and the store and passes plain values in.
 *
 * @module searchResultsMap/services/buildSnapshot
 */

import { areBoundsValid } from './geometry';
import { getSearchBounds } from './sitePlanViewportPadding';
import deriveViewportSearchMode from './deriveViewportSearchMode';

/**
 * @param {object} values
 * @param {{ zoom: number, bounds: { south: number, north: number, west: number, east: number } }} values.camera
 * @param {boolean} values.hasUserGesture - A real gesture happened since the previous settled position.
 * @param {boolean} values.isInitialPosition - First settled position after start or a location selection.
 * @param {string} values.locationType
 * @param {string} values.resultType
 * @param {boolean} values.focusedSearchActive
 * @param {object|null} values.location
 * @param {Array|null} values.sitePlans
 * @returns {Readonly<object>|null} The snapshot, or null when the camera bounds are not valid.
 *   `viewport` is what the user sees; `searchBounds` is what a viewport-driven search
 *   requests (see ./sitePlanViewportPadding.js).
 */
export default function buildSnapshot({
  camera,
  hasUserGesture,
  isInitialPosition,
  locationType,
  resultType,
  focusedSearchActive,
  location,
  sitePlans,
}) {
  const viewport = camera?.bounds;
  if (!areBoundsValid(viewport)) {
    return null;
  }

  const zoom = camera.zoom ?? 0;
  const loadedSitePlans = sitePlans ?? [];

  return Object.freeze({
    zoom,
    viewport,
    searchBounds: getSearchBounds(viewport, zoom, loadedSitePlans),
    hasUserGesture: hasUserGesture === true,
    isInitialPosition: isInitialPosition === true,
    locationType,
    resultType,
    mode: deriveViewportSearchMode({ locationType, focusedSearchActive }),
    location: location ?? null,
    sitePlans: loadedSitePlans,
  });
}
